import type { ReplayEnvelopeV1 } from '@cars/shared';
import { AssetLoader } from '../assets/AssetLoader';
import { ThreeRaceRenderer } from '../rendering/ThreeRaceRenderer';
import { LiveSession } from '../runtime/LiveSession';
import { ReplaySession } from '../runtime/ReplaySession';
import { MenuController } from './MenuController';
import { FrameClock } from './FrameClock';

export interface GameData {
  trackId?: number;
  carId?: number;
  username?: string;
  showScoreboard?: boolean;
  startupError?: string;
  replay?: ReplayEnvelopeV1;
}

export interface ApplicationHost {
  renderer: ThreeRaceRenderer;
  assets: AssetLoader;
  signal: AbortSignal;
  uiRoot: HTMLElement;
  isActive(): boolean;
  resetFrameClock(): void;
  startRace(data: GameData): void;
  showReplay(data: GameData): void;
  showMenu(data: GameData): void;
}

interface ApplicationSession {
  create(data: GameData): void | Promise<void>;
  update(delta: number): void;
  resize(): void;
  dispose(): void;
}

type View = 'menu' | 'live' | 'replay';

/** One animation loop, one renderer, one active session and one cancellable load. */
export class GameApplication {
  private readonly container: HTMLElement;
  private readonly uiRoot: HTMLElement;
  private readonly assets = new AssetLoader();
  private renderer: ThreeRaceRenderer | null = null;
  private session: ApplicationSession | null = null;
  private controller = new AbortController();
  private readonly resizeObserver: ResizeObserver;
  private frameId = 0;
  private readonly frameClock = new FrameClock();
  private generation = 0;
  private disposed = false;
  private started = false;
  private suspended = false;
  private username = '';
  private readonly onPageHide = (event: PageTransitionEvent): void => {
    if (!event.persisted) {
      this.dispose();
      return;
    }
    // A restored page must never continue an abandoned competitive attempt.
    this.suspended = true;
    cancelAnimationFrame(this.frameId);
    this.generation += 1;
    this.controller.abort();
    this.session?.dispose();
    this.session = null;
    this.renderer?.clear();
    this.uiRoot.replaceChildren();
  };
  private readonly onPageShow = (): void => {
    if (this.disposed || !this.suspended) return;
    this.suspended = false;
    this.navigate('menu', { username: this.username, showScoreboard: true });
    this.frameId = requestAnimationFrame(this.frame);
  };
  private readonly onResize = (): void => {
    this.renderer?.resize();
    this.session?.resize();
  };
  private readonly frame = (time: number): void => {
    if (this.disposed || this.suspended) return;
    const delta = this.frameClock.sample(time);
    try {
      if (delta !== null) this.session?.update(delta);
    } catch (error) {
      this.fail(error);
    }
    this.frameId = requestAnimationFrame(this.frame);
  };

  constructor(container: HTMLElement, uiRoot: HTMLElement) {
    this.container = container;
    this.uiRoot = uiRoot;
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(container);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
  }

  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;
    this.navigate('menu', {});
    this.frameId = requestAnimationFrame(this.frame);
  }

  private navigate(view: View, data: GameData): void {
    if (this.disposed) return;
    this.generation += 1;
    const generation = this.generation;
    this.controller.abort();
    this.session?.dispose();
    this.session = null;
    this.controller = new AbortController();
    this.renderer?.clear();
    this.uiRoot.replaceChildren();
    this.frameClock.reset(performance.now());
    this.username = data.username ?? this.username;
    try {
      const renderer =
        this.renderer ??
        new ThreeRaceRenderer(this.container, (message) => this.fail(new Error(message)));
      this.renderer = renderer;
      if (view !== 'menu') this.showLoading();
      const signal = this.controller.signal;
      const host: ApplicationHost = {
        renderer,
        assets: this.assets,
        signal,
        uiRoot: this.uiRoot,
        isActive: () => !this.disposed && !signal.aborted && generation === this.generation,
        resetFrameClock: () => {
          if (generation === this.generation) this.frameClock.reset(performance.now());
        },
        startRace: (data) => this.navigate('live', data),
        showReplay: (data) => this.navigate('replay', data),
        showMenu: (data) => this.navigate('menu', data),
      };
      const session =
        view === 'menu'
          ? new MenuController(host)
          : view === 'live'
            ? new LiveSession(host)
            : new ReplaySession(host);
      this.session = session;
      void Promise.resolve(session.create(data)).catch((error) => {
        if (host.isActive()) this.fail(error);
      });
    } catch (error) {
      if (generation === this.generation) this.fail(error);
    }
  }

  private showLoading(): void {
    const panel = document.createElement('section');
    panel.className = 'ui-screen';
    panel.setAttribute('role', 'status');
    panel.textContent = 'Loading race assets… ';
    const cancel = document.createElement('button');
    cancel.className = 'ui-button';
    cancel.textContent = 'Return to Main Screen';
    cancel.addEventListener('click', () =>
      this.navigate('menu', { username: this.username, showScoreboard: true }),
    );
    panel.append(cancel);
    this.uiRoot.append(panel);
  }

  private fail(error: unknown): void {
    this.generation += 1;
    this.controller.abort();
    this.session?.dispose();
    this.session = null;
    this.renderer?.dispose();
    this.renderer = null;
    const panel = document.createElement('section');
    panel.className = 'ui-screen';
    panel.setAttribute('role', 'alert');
    const message = document.createElement('p');
    message.textContent = error instanceof Error ? error.message : 'Unable to load the game.';
    const retry = document.createElement('button');
    retry.className = 'ui-button';
    retry.textContent = 'Return to Main Screen';
    retry.addEventListener('click', () =>
      this.navigate('menu', { username: this.username, showScoreboard: true }),
    );
    panel.append(message, retry);
    this.uiRoot.replaceChildren(panel);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.generation += 1;
    cancelAnimationFrame(this.frameId);
    this.controller.abort();
    this.session?.dispose();
    this.session = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    this.uiRoot.replaceChildren();
  }
}
